// src/pages/notifications/CreateNotificationPage.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Stack,
} from '@mui/material';
import {
  Send as SendIcon,
  Preview as PreviewIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useNavigationStack } from '../../../context/NavigationStackContext';

// Components
import RecipientSelector from '../components/RecipientSelector';
import NotificationForm from '../components/NotificationForm';
import NotificationPreview from '../components/NotificationPreview';
import NotificationTypeGuide from '../components/NotificationTypeGuide';

// Hooks
import { useNotificationsManagement } from '../hooks/useNotificationsManagement';
import { useStaff } from '../../staff/hooks/useStaff';
import { useDepartments } from '../../departments/hooks/useDepartment';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { useAuth } from '../../../context/AuthContext';
import { FormPage, FormActions } from '../../../components/common/form';

export default function CreateNotificationPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { navigateBack } = useNavigationStack();
  const { user } = useAuth();
  const { showSuccess, showError } = useSnackbar();

  // State for form
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    notification_type: 'info',
    priority: 'normal',
    onclick_url: '',
    expires_at: null,
    department_id: user?.department_id || '',
  });

  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [isPreviewMode, setIsPreviewMode] = useState(false);

  // API hooks
  const {
    createBulkNotifications,
    loading: notificationLoading,
    error: notificationError,
    clearError,
  } = useNotificationsManagement();

  const {
    staff,
    fetchDepartmentStaff,
    loading: staffLoading,
  } = useStaff();

  const {
    departments,
    fetchAllDepartments,
    loading: departmentsLoading,
  } = useDepartments();

  // Load data on mount
  useEffect(() => {
    fetchDepartmentStaff({ include_inactive: true });
    fetchAllDepartments({ include_deactivated: false });
  }, [fetchDepartmentStaff, fetchAllDepartments]);

  // Memoized callbacks to prevent re-renders
  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear validation error for this field
    setValidationErrors(prev => {
      if (prev[field]) {
        const { [field]: removed, ...rest } = prev;
        return rest;
      }
      return prev;
    });
  }, []);

  const handleDateChange = useCallback((date) => {
    setFormData(prev => ({ ...prev, expires_at: date }));
  }, []);

  const handleRecipientsChange = useCallback((recipients) => {
    setSelectedRecipients(recipients);
    setValidationErrors(prev => {
      if (prev.recipients) {
        const { recipients: removed, ...rest } = prev;
        return rest;
      }
      return prev;
    });
  }, []);

  // Validation function
  const isValidUrl = useCallback((url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }, []);

  const validateForm = useCallback(() => {
    const errors = {};

    if (!formData.title.trim()) {
      errors.title = t('notifications:validation.title_required');
    } else if (formData.title.length > 255) {
      errors.title = t('notifications:validation.title_max_length');
    }

    if (!formData.message.trim()) {
      errors.message = t('notifications:validation.message_required');
    }

    if (selectedRecipients.length === 0) {
      errors.recipients = t('notifications:validation.recipients_required');
    }

    if (formData.onclick_url && !isValidUrl(formData.onclick_url)) {
      errors.onclick_url = t('notifications:validation.url_invalid');
    }

    if (formData.expires_at && new Date(formData.expires_at) <= new Date()) {
      errors.expires_at = t('notifications:validation.expires_future');
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, selectedRecipients, isValidUrl, t]);

  // Handle form submission
  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      // Always use bulk notification for consistency
      const bulkData = {
        recipient_ids: selectedRecipients.map(r => r.staff_member_id),
        department_id: formData.department_id,
        title: formData.title,
        message: formData.message,
        notification_type: formData.notification_type,
        priority: formData.priority,
        onclick_url: formData.onclick_url || null,
        expires_at: formData.expires_at ? formData.expires_at.toISOString() : null,
      };

      const result = await createBulkNotifications(bulkData);

      if (result.success) {
        showSuccess(
          t('notifications:success.sent', { 
            count: selectedRecipients.length 
          })
        );
        navigate('/notifications');
      } else {
        showError(result.error || t('notifications:errors.send_failed'));
      }
    } catch (error) {
      showError(t('notifications:errors.unexpected_error'));
    }
  }, [validateForm, selectedRecipients, formData, createBulkNotifications, showSuccess, showError, navigate, t]);

  const togglePreview = useCallback(() => {
    setIsPreviewMode(prev => !prev);
  }, []);

  const handleCancel = useCallback(() => {
    navigateBack('/notifications');
  }, [navigateBack]);

  // Memoize computed values
  const isLoading = useMemo(() => 
    notificationLoading || staffLoading || departmentsLoading, 
    [notificationLoading, staffLoading, departmentsLoading]
  );

  const canSubmit = useMemo(() => 
    !isLoading && selectedRecipients.length > 0, 
    [isLoading, selectedRecipients.length]
  );

  const submitButtonText = useMemo(() => {
    if (isLoading) return t('notifications:actions.sending');
    return t('notifications:actions.send_to_recipients', { 
      count: selectedRecipients.length 
    });
  }, [isLoading, selectedRecipients.length, t]);

  return (
    <FormPage
      title={t('notifications:create_notification')}
      subtitle={t('notifications:create_subtitle')}
      onBack={handleCancel}
      onSubmit={handleSubmit}
      fieldErrors={validationErrors}
      error={notificationError}
      onErrorClose={clearError}
      maxWidth={isPreviewMode ? 1200 : undefined}
    >
      <Box sx={{ display: 'flex', gap: 6, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <Stack spacing={5} sx={{ flex: '1 1 480px', minWidth: 0 }}>
          <RecipientSelector
            selectedRecipients={selectedRecipients}
            onRecipientsChange={handleRecipientsChange}
            departments={departments}
            allStaff={staff}
            loading={isLoading}
            validationError={validationErrors.recipients}
          />
          <NotificationForm
            formData={formData}
            onInputChange={handleInputChange}
            onDateChange={handleDateChange}
            validationErrors={validationErrors}
            loading={isLoading}
          />
        </Stack>

        {isPreviewMode && (
          <Box sx={{ flex: '0 1 380px', minWidth: 280, position: 'sticky', top: 16 }}>
            <NotificationPreview
              formData={formData}
              recipientCount={selectedRecipients.length}
            />
          </Box>
        )}
      </Box>

      <FormActions
        onCancel={handleCancel}
        cancelLabel={t('notifications:actions.cancel')}
        submitType="submit"
        submitLabel={submitButtonText}
        submitIcon={<SendIcon />}
        loading={isLoading}
        disabled={!canSubmit}
      >
        <Button variant="text" startIcon={<PreviewIcon />} onClick={togglePreview} sx={{ mr: 'auto' }}>
          {isPreviewMode ? t('notifications:actions.hide_preview') : t('notifications:actions.preview')}
        </Button>
      </FormActions>
    </FormPage>
  );
}
